/**
 * Reiter „So lerne ich“ der Seite Gedächtnis (Lernen S3, Antoine 27.09.2026; Mockup im Konzept
 * ~/maestro-uebergabe/konzept-lernen.html).
 *
 * Links: was MAESTRO aus deinen Änderungen an KI-Entwürfen schließt — je Schluss „X von Y“ als
 * Balken, Belege zum Anklicken und die Knöpfe Stimmt / Stimmt nicht / Bearbeiten / Pausieren /
 * Verlauf. Rechts: „KI-Vorschlag → deine Fassung“ je Angebot als Diff mit Kontext-Chips und
 * „Daraus gelernt …“.
 *
 * Die Zahlen zählt der Server (lib/lern-muster.ts), nicht das Modell und nicht diese Seite; die
 * Übergänge (ein Preis-Muster wird nie „Stimmt“, eine Beobachtung unter der Schwelle wirkt nie)
 * prüft ebenfalls der Server — hier werden nur die Knöpfe gezeigt, die er annehmen würde.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Card, Drawer, EmptyState, ErrorNote, Icon, LoadingRows, Textarea, Zeichenzaehler } from "../ui/components";
import { useContentRefetch } from "../ui/CopilotProvider";
import { LernKennzahlKarte, type LernKennzahl } from "./gedaechtnis-ueberblick";

export type SchlussStatus = "beobachtet" | "offen" | "aktiv" | "abgelehnt" | "pausiert" | "als_einstellung" | "entfernt";
export interface Schluss {
  id: string;
  art: "auswahl" | "preis" | "eigen";
  status: SchlussStatus;
  text: string;
  grundtext: string;
  treffer: number;
  von: number;
  belege: { eventId: string; titel: string }[];
  gegenbelege: { eventId: string; titel: string }[];
  kontext: string[];
  bearbeitet: boolean;
  entschiedenAm: string | null;
  version: number;
  /** Lernen S4: feste Nummer (R3) eines aktivierten Auswahl-Schlusses. */
  nummer?: number | null;
  /** Lernen S4: Quote vor/nach der Aktivierung (Server, lib/lern-muster.ts#ladeWirkung). */
  wirkung?: SchlussWirkung | null;
}
export interface SchlussWirkung {
  aktiviertAm: string;
  angewendetIn: number;
  vorher: { unveraendert: number; von: number };
  nachher: { unveraendert: number; von: number };
}
export interface PreisRundungRegel {
  schrittCents: number;
  richtung: "auf" | "ab";
  ausSchlussId: string | null;
}
export interface LernFall {
  eventId: string;
  titel: string;
  gesendetAm: string | null;
  chips: string[];
  zeilen: { op: "-" | "+" | "=" | "~"; name: string; gang: string | null; detail: string }[];
  proGastKiCents: number;
  proGastGesendetCents: number;
  unveraendert: boolean;
  lernenAusgeschlossen: boolean;
  gelernt: { id: string; text: string; treffer: number; von: number; status: SchlussStatus }[];
}
export interface LernSeite {
  schluesse: Schluss[];
  faelle: LernFall[];
  schwelle: { minBelege: number; minQuote: number };
  kennzahl: LernKennzahl | null;
  /** Gesendete KI-Angebote, die zählen (ohne Tests und „Nicht zum Lernen verwenden“). */
  ausgewertet: number;
  /** Lernen S4: die Rundungsregel der Preis-Engine (null = keine Regel oder Migration fehlt). */
  rundung?: PreisRundungRegel | null;
}
interface VerlaufEintrag {
  version: number;
  aktion: string;
  status: string;
  text: string;
  vorherStatus: string | null;
  vorherText: string | null;
  von: string | null;
  am: string;
}

const euro = (c: number) => `${(c / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const PILLE = "inline-flex items-center rounded-full px-2.5 py-0.5 font-label-md text-[11.5px] font-semibold";
const TON = {
  akzent: "bg-primary-container text-on-primary-container",
  gut: "bg-tertiary-container text-on-tertiary-container",
  warn: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  neutral: "bg-surface-container-high text-on-surface-variant dark:bg-white/10",
} as const;

export function statusPille(s: Pick<Schluss, "art" | "status" | "wirkung">, alsRegelAktiv = false): { text: string; ton: keyof typeof TON } {
  if (s.art === "eigen") return s.status === "pausiert" ? { text: "Dein Hinweis · pausiert", ton: "neutral" } : { text: "Dein Hinweis · wirkt beim Komponieren", ton: "gut" };
  if (s.art === "preis") {
    if (s.status === "offen") return { text: "Vorschlag · wird eine Einstellung", ton: "warn" };
    if (s.status === "als_einstellung") return alsRegelAktiv ? { text: "Einstellung · rundet neue KI-Entwürfe", ton: "gut" } : { text: "Als Einstellung vorgemerkt", ton: "neutral" };
  }
  switch (s.status) {
    case "offen":
      return { text: "Neu · wartet auf dich", ton: "akzent" };
    case "aktiv":
      return s.wirkung
        ? { text: `Aktiv · angewendet in ${s.wirkung.angewendetIn} ${s.wirkung.angewendetIn === 1 ? "Entwurf" : "Entwürfen"}`, ton: "gut" }
        : { text: "Aktiv · wirkt beim Komponieren", ton: "gut" };
    case "pausiert":
      return { text: "Pausiert", ton: "neutral" };
    case "abgelehnt":
      return { text: "Stimmt nicht · kommt nicht wieder", ton: "neutral" };
    default:
      return { text: "Beobachtet · zu wenig Belege", ton: "neutral" };
  }
}

const AKTION_LABEL: Record<string, string> = {
  angelegt: "angelegt",
  stimmt: "„Stimmt“",
  stimmt_nicht: "„Stimmt nicht“",
  pausieren: "pausiert",
  fortsetzen: "fortgesetzt",
  als_einstellung: "als Einstellung vorgemerkt",
  bearbeiten: "Text geändert",
  entfernen: "entfernt",
  rueckgaengig: "rückgängig gemacht",
};

const KNOPF = "inline-flex min-h-[36px] items-center gap-1 rounded-full border border-outline-variant px-3 py-1.5 text-[12.5px] font-semibold text-on-background disabled:opacity-40 dark:border-white/10 dark:text-inverse-on-surface";
const KNOPF_PRIMAER = "inline-flex min-h-[36px] items-center gap-1 rounded-full border border-primary bg-primary px-3 py-1.5 text-[12.5px] font-semibold text-on-primary disabled:opacity-40";

function Balken({ treffer, von }: { treffer: number; von: number }) {
  const anteil = von > 0 ? Math.round((treffer / von) * 100) : 0;
  return (
    <span className="inline-block h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-surface-container-high dark:bg-white/10" aria-hidden="true">
      <span className="block h-full rounded-full bg-primary" style={{ width: `${anteil}%` }} />
    </span>
  );
}

const SCHRITT_TEXT: Record<number, string> = { 100: "volle Euro", 500: "volle 5 €", 1000: "volle 10 €" };
// Geschützte Leerzeichen: „(82 %)“ bricht nie auseinander.
const quote = (x: { unveraendert: number; von: number }) => (x.von > 0 ? `\u00a0(${Math.round((x.unveraendert / x.von) * 100)}\u00a0%)` : "");

/** Lernen S4: „vor der Aktivierung 3 von 8 unverändert · seit Aktivierung 9 von 11“. */
function WirkungZeile({ w }: { w: SchlussWirkung }) {
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2" data-testid="lern-wirkung">
      <div className="rounded-xl bg-surface-container-low px-3 py-2 dark:bg-white/[0.04]">
        <p className="text-[11.5px] text-on-surface-variant">Vor der Aktivierung</p>
        <p className="text-[13px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">
          {w.vorher.von > 0 ? `${w.vorher.unveraendert} von ${w.vorher.von} unverändert${quote(w.vorher)}` : "keine vergleichbaren Angebote"}
        </p>
      </div>
      <div className="rounded-xl bg-tertiary-container/40 px-3 py-2 dark:bg-tertiary/10">
        <p className="text-[11.5px] text-on-surface-variant">Seit {new Date(w.aktiviertAm).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}, wo angewendet</p>
        <p className="text-[13px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">
          {w.nachher.von > 0 ? `${w.nachher.unveraendert} von ${w.nachher.von} unverändert gesendet${quote(w.nachher)}` : "noch nichts gesendet"}
        </p>
      </div>
    </div>
  );
}

function SchlussKarte({
  s,
  schwelle,
  rundung,
  onFertig,
  onBeleg,
  onVerlauf,
}: {
  s: Schluss;
  schwelle: LernSeite["schwelle"];
  rundung?: PreisRundungRegel | null;
  onFertig: () => void;
  onBeleg: (eventId: string) => void;
  onVerlauf: (s: Schluss) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [belegeOffen, setBelegeOffen] = useState(false);
  async function tu(body: Record<string, unknown>) {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/gedaechtnis/lernen/${s.id}`, "POST", body);
      setText(null);
      onFertig();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const alsRegelAktiv = s.art === "preis" && s.status === "als_einstellung" && rundung?.ausSchlussId === s.id;
  const pille = statusPille(s, alsRegelAktiv);
  const gedaempft = s.status === "beobachtet" || s.status === "abgelehnt" || s.status === "pausiert";
  const knopf = (label: string, icon: string, body: Record<string, unknown>, primaer = false) => (
    <button type="button" className={primaer ? KNOPF_PRIMAER : KNOPF} disabled={busy} onClick={() => void tu(body)}>
      <Icon name={icon} className="text-[15px]" /> {label}
    </button>
  );
  return (
    <div className={`rounded-2xl border border-outline-variant px-4 py-3.5 dark:border-white/10 ${gedaempft ? "bg-surface-container-low/60 dark:bg-white/[0.02]" : "bg-surface-container-lowest dark:bg-white/[0.04]"}`} data-testid="lern-schluss" data-status={s.status}>
      <div className="flex flex-wrap items-center gap-1.5">
        {s.nummer != null && <span className={`${PILLE} bg-primary text-on-primary`} title="Unter dieser Nummer meldet der Komponist, dass er den Schluss angewendet hat">R{s.nummer}</span>}
        <span className={`${PILLE} ${TON[pille.ton]}`}>{pille.text}</span>
      </div>
      {text === null ? (
        <p className={`mt-2 font-body-md text-body-md font-semibold ${gedaempft ? "text-on-surface-variant" : "text-on-background dark:text-inverse-on-surface"}`}>{s.text}</p>
      ) : (
        <form
          className="mt-2 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim().length >= 3) void tu({ aktion: "bearbeiten", text: text.trim() });
          }}
        >
          <Textarea rows={3} maxLength={300} value={text} onChange={(e) => setText(e.target.value)} aria-label="Text des Schlusses" />
          <Zeichenzaehler current={text.length} max={300} />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy || text.trim().length < 3}>Speichern</Button>
            <Button type="button" variant="secondary" onClick={() => setText(null)}>Abbrechen</Button>
          </div>
        </form>
      )}
      {s.art !== "eigen" && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-on-surface-variant">
          <Balken treffer={s.treffer} von={s.von} />
          <span className="font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">{s.treffer} von {s.von} Angeboten</span>
          {s.kontext.length > 0 && <span>{s.kontext.join(" · ")}</span>}
          {s.belege.length > 0 && (
            <button type="button" className="font-semibold text-primary hover:underline" aria-expanded={belegeOffen} onClick={() => setBelegeOffen((v) => !v)}>
              {belegeOffen ? "Belege ausblenden" : "Belege ansehen"}
            </button>
          )}
        </div>
      )}
      {belegeOffen && (
        <ul className="mt-2 space-y-1 rounded-xl bg-surface-container-low p-2.5 text-[12.5px] dark:bg-white/[0.04]">
          {s.belege.map((b) => (
            <li key={b.eventId} className="flex min-w-0 items-center gap-2">
              <Icon name="check_small" className="shrink-0 text-[16px] text-tertiary" />
              <button type="button" className="min-w-0 truncate text-left font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface" onClick={() => onBeleg(b.eventId)}>
                {b.titel}
              </button>
              <Link to={`/vorgaenge/${b.eventId}`} className="ml-auto shrink-0 text-primary hover:underline" aria-label={`Vorgang ${b.titel} öffnen`}>
                <Icon name="open_in_new" className="text-[15px]" />
              </Link>
            </li>
          ))}
          {s.gegenbelege.length > 0 && (
            <li className="pt-1 text-on-surface-variant">
              Nicht geändert in {s.gegenbelege.length} {s.gegenbelege.length === 1 ? "Angebot" : "Angeboten"}: {s.gegenbelege.map((b) => b.titel).join(", ")}
            </li>
          )}
        </ul>
      )}
      {s.status === "beobachtet" && (
        <p className="mt-1.5 text-[12px] text-on-surface-variant">Ich mache daraus erst ab {schwelle.minBelege} Belegen und {Math.round(schwelle.minQuote * 100)} % Übereinstimmung einen Schluss. Bis dahin wirkt es nicht.</p>
      )}
      {s.art === "auswahl" && s.status === "offen" && <p className="mt-1.5 text-[12px] text-on-surface-variant">Wirkt erst, wenn du „Stimmt“ sagst — dann als Hinweis beim Komponieren, neben den ähnlichen Fällen.</p>}
      {s.art === "preis" && s.status === "offen" && <p className="mt-1.5 text-[12px] text-on-surface-variant">Preise ändert nie das Modell. Übernommen wird das als Einstellung für die Preise, nicht als Regel für die KI.</p>}
      {s.wirkung && s.status !== "beobachtet" && <WirkungZeile w={s.wirkung} />}
      {s.art === "preis" && s.status === "als_einstellung" && (alsRegelAktiv && rundung ? (
        <p className="mt-1.5 text-[12px] text-on-surface-variant">
          In den Preiseinstellungen: {rundung.richtung === "auf" ? "aufrunden" : "abrunden"} auf {SCHRITT_TEXT[rundung.schrittCents] ?? `${rundung.schrittCents / 100} €`}. Die Preis-Engine rechnet das in jeden neuen KI-Entwurf ein — die KI setzt nie Preise.{" "}
          <Link to="/einstellungen?tab=zahlungen" className="font-semibold text-primary hover:underline">Einstellung ansehen</Link>
        </p>
      ) : (
        <p className="mt-1.5 text-[12px] text-on-surface-variant">Vorgemerkt für die Preiseinstellungen. Bis dort eine Rundungsregel gilt, rundest du weiter selbst — die KI setzt nie Preise.</p>
      ))}
      {fehler && <p className="mt-1.5 font-body-sm text-body-sm text-error">{fehler}</p>}
      {text === null && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {s.art === "auswahl" && s.status === "offen" && knopf("Stimmt", "check", { aktion: "stimmt" }, true)}
          {s.art === "preis" && s.status === "offen" && knopf("Als Einstellung übernehmen", "tune", { aktion: "als_einstellung" }, true)}
          {s.status === "pausiert" && knopf("Fortsetzen", "play_arrow", { aktion: "fortsetzen" }, true)}
          {s.art !== "eigen" && s.status !== "abgelehnt" && s.status !== "als_einstellung" && knopf("Stimmt nicht", "close", { aktion: "stimmt_nicht" })}
          {(s.status === "offen" || s.status === "aktiv" || s.status === "pausiert") && s.art !== "preis" && (
            <button type="button" className={KNOPF} disabled={busy} onClick={() => setText(s.text)}>
              <Icon name="edit" className="text-[15px]" /> Bearbeiten
            </button>
          )}
          {s.status === "aktiv" && knopf("Pausieren", "pause", { aktion: "pausieren" })}
          {s.art === "eigen" && knopf("Entfernen", "delete", { aktion: "entfernen" })}
          {(s.entschiedenAm || s.art === "eigen") && (
            <button type="button" className={KNOPF} onClick={() => onVerlauf(s)}>
              <Icon name="history" className="text-[15px]" /> Verlauf
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function VerlaufDrawer({ s, onClose, onFertig }: { s: Schluss | null; onClose: () => void; onFertig: () => void }) {
  const q = useApi<VerlaufEintrag[]>(s ? `/api/gedaechtnis/lernen/${s.id}/verlauf` : null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  async function rueckgaengig() {
    if (!s) return;
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/gedaechtnis/lernen/${s.id}`, "POST", { aktion: "rueckgaengig" });
      q.reload();
      onFertig();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Drawer open={!!s} onClose={onClose} title="Verlauf" subtitle={s?.text} breite="schmal">
      {q.error ? (
        <ErrorNote message={q.error} onRetry={q.reload} />
      ) : !q.data ? (
        <LoadingRows rows={3} />
      ) : q.data.length === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Noch keine Änderung — der Schluss steht so, wie MAESTRO ihn gezählt hat.</p>
      ) : (
        <ol className="space-y-3">
          {q.data.map((v, i) => (
            <li key={v.version} className="rounded-xl border border-outline-variant px-3 py-2.5 dark:border-white/10">
              <p className="text-[12px] text-on-surface-variant">
                {new Date(v.am).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}
                {v.von ? ` · ${v.von}` : ""}
              </p>
              <p className="mt-0.5 font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{AKTION_LABEL[v.aktion] ?? v.aktion}</p>
              {v.vorherText && v.vorherText !== v.text && (
                <p className="mt-1 text-[12.5px] text-on-surface-variant">
                  <s>{v.vorherText}</s>
                  <br />
                  {v.text}
                </p>
              )}
              {i === 0 && (
                <button type="button" className={`${KNOPF} mt-2`} disabled={busy} onClick={() => void rueckgaengig()}>
                  <Icon name="undo" className="text-[15px]" /> Rückgängig
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
      {fehler && <p className="mt-2 font-body-sm text-body-sm text-error">{fehler}</p>}
    </Drawer>
  );
}

const DIFF_STIL: Record<LernFall["zeilen"][number]["op"], { zeichen: string; klasse: string; label: string }> = {
  "-": { zeichen: "−", klasse: "bg-error-container/50 text-on-error-container dark:bg-error/15 dark:text-inverse-on-surface", label: "entfernt" },
  "+": { zeichen: "+", klasse: "bg-tertiary-container text-on-tertiary-container dark:bg-tertiary/20 dark:text-inverse-on-surface", label: "ergänzt" },
  "=": { zeichen: "=", klasse: "text-on-background dark:text-inverse-on-surface", label: "unverändert" },
  "~": { zeichen: "~", klasse: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]", label: "Menge geändert" },
};

function FallAnsicht({ faelle, index, setIndex, onFertig }: { faelle: LernFall[]; index: number; setIndex: (i: number) => void; onFertig: () => void }) {
  const f = faelle[index];
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  if (!f) return null;
  async function schalten(ausgeschlossen: boolean) {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${f!.eventId}/lernen`, "PUT", { ausgeschlossen });
      onFertig();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const aktiveGelernt = f.gelernt.filter((g) => g.status !== "abgelehnt");
  return (
    <div className="space-y-3" data-testid="lern-fall">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/vorgaenge/${f.eventId}`} className="font-body-md text-body-md font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface">{f.titel}</Link>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {f.chips.map((c) => (
              <span key={c} className="rounded-full bg-surface-container-high px-2 py-0.5 text-[11.5px] text-on-surface-variant dark:bg-white/10">{c}</span>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button variant="ghost" className="!px-2" disabled={index === 0} onClick={() => setIndex(index - 1)} aria-label="Neueres Angebot"><Icon name="chevron_left" /></Button>
          <span className="text-[12px] tabular-nums text-on-surface-variant">{index + 1} von {faelle.length}</span>
          <Button variant="ghost" className="!px-2" disabled={index >= faelle.length - 1} onClick={() => setIndex(index + 1)} aria-label="Älteres Angebot"><Icon name="chevron_right" /></Button>
        </div>
      </div>
      {f.lernenAusgeschlossen && <p className="rounded-xl bg-surface-container-high px-3 py-2 text-[12.5px] text-on-surface-variant dark:bg-white/10">Dieses Angebot ist vom Lernen ausgeschlossen und zählt nirgends mit.</p>}
      <ul className="overflow-hidden rounded-xl border border-outline-variant dark:border-white/10">
        {f.zeilen.map((z, i) => (
          <li key={`${z.op}-${z.name}-${i}`} className={`grid grid-cols-[18px_minmax(0,1fr)_auto] items-baseline gap-2 border-t border-outline-variant/40 px-3 py-2 text-[13px] first:border-t-0 dark:border-white/10 ${DIFF_STIL[z.op].klasse}`}>
            <span className="font-bold" aria-label={DIFF_STIL[z.op].label}>{DIFF_STIL[z.op].zeichen}</span>
            <span className="min-w-0 break-words">{z.name}</span>
            <span className="whitespace-nowrap text-right tabular-nums text-[12px] opacity-80">{z.detail}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-baseline justify-between gap-2 border-t border-outline-variant/40 pt-2 text-[13px] dark:border-white/10">
        <span className="text-on-surface-variant">Pro Gast</span>
        <span className="tabular-nums">
          {f.proGastKiCents !== f.proGastGesendetCents && <s className="mr-2 text-on-surface-variant">{euro(f.proGastKiCents)}</s>}
          <b className="text-on-background dark:text-inverse-on-surface">{euro(f.proGastGesendetCents)}</b>
        </span>
      </div>
      <div className="rounded-xl bg-surface-container-low px-3 py-2.5 text-[12.5px] dark:bg-white/[0.04]">
        <p className="font-semibold text-on-background dark:text-inverse-on-surface">Daraus gelernt</p>
        <p className="mt-0.5 text-on-surface-variant">
          {f.lernenAusgeschlossen
            ? "Nichts — du hast es vom Lernen ausgeschlossen."
            : f.unveraendert
              ? "Du hast den KI-Entwurf unverändert gesendet. Das zählt für die Quote oben."
              : aktiveGelernt.length === 0
                ? "Die Änderungen stützen noch keinen Schluss — dafür braucht es mehr ähnliche Angebote."
                : `Stützt ${aktiveGelernt.map((g) => `„${g.text.replace(/\.$/, "")}“ (${g.treffer} von ${g.von})`).join(" und ")}.`}
        </p>
      </div>
      {fehler && <p className="font-body-sm text-body-sm text-error">{fehler}</p>}
      <button type="button" className={KNOPF} disabled={busy} onClick={() => void schalten(!f.lernenAusgeschlossen)}>
        <Icon name={f.lernenAusgeschlossen ? "school" : "block"} className="text-[15px]" />
        {f.lernenAusgeschlossen ? "Wieder zum Lernen verwenden" : "Dieses Angebot nicht zum Lernen verwenden"}
      </button>
    </div>
  );
}

function HinweisForm({ onFertig, onAbbrechen }: { onFertig: () => void; onAbbrechen: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  return (
    <form
      className="space-y-2 rounded-2xl border border-primary/40 bg-surface-container-lowest px-4 py-3.5 dark:bg-white/[0.04]"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setFehler(null);
        try {
          await apiMutate("/api/gedaechtnis/lernen", "POST", { text: text.trim() });
          onFertig();
        } catch (err) {
          setFehler(err instanceof Error ? err.message : String(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface" htmlFor="eigener-hinweis">Wie machst du es?</label>
      <Textarea id="eigener-hinweis" rows={3} maxLength={300} placeholder="z. B. Bei Hochzeiten immer ein Dessert-Buffet statt Tiramisù." value={text} onChange={(e) => setText(e.target.value)} />
      <Zeichenzaehler current={text.length} max={300} />
      <p className="text-[12px] text-on-surface-variant">Wirkt sofort als Hinweis beim Komponieren. Keine Preise, Allergene oder Pflichtangaben — die kommen aus Katalog und Einstellungen.</p>
      {fehler && <p className="font-body-sm text-body-sm text-error">{fehler}</p>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={busy || text.trim().length < 3}>Hinweis speichern</Button>
        <Button type="button" variant="secondary" onClick={onAbbrechen}>Abbrechen</Button>
      </div>
    </form>
  );
}

export function LernenTab({ neuerHinweis, onHinweisZu }: { neuerHinweis: boolean; onHinweisZu: () => void }) {
  const q = useApi<LernSeite | null>("/api/gedaechtnis/lernen");
  useContentRefetch("knowledge", () => q.reload());
  const [index, setIndex] = useState(0);
  const [verlauf, setVerlauf] = useState<Schluss | null>(null);
  const [zeigeAbgelehnt, setZeigeAbgelehnt] = useState(false);
  const fallRef = useRef<HTMLDivElement>(null);
  const faelle = q.data?.faelle ?? [];
  useEffect(() => {
    if (index > 0 && index >= faelle.length) setIndex(0);
  }, [faelle.length, index]);

  if (q.error) return <Card className="overflow-hidden"><ErrorNote zentriert title="„So lerne ich“ konnte nicht geladen werden" message={q.error} onRetry={q.reload} /></Card>;
  if (q.loading && !q.data) return <Card className="overflow-hidden"><LoadingRows rows={4} /></Card>;
  if (!q.data) {
    return (
      <Card className="overflow-hidden">
        <EmptyState icon="school" title="Noch nicht eingerichtet" hint="Die Muster brauchen ein Datenbank-Update, das noch aussteht. Bis dahin lernt MAESTRO weiter aus den ähnlichen Fällen." />
      </Card>
    );
  }
  const d = q.data;
  const sichtbar = d.schluesse.filter((s) => s.status !== "abgelehnt");
  const abgelehnt = d.schluesse.filter((s) => s.status === "abgelehnt");
  const aktiv = d.schluesse.filter((s) => s.status === "aktiv").length;
  const offen = d.schluesse.filter((s) => s.status === "offen").length;
  const zeigeFall = (eventId: string) => {
    const i = faelle.findIndex((f) => f.eventId === eventId);
    if (i >= 0) {
      setIndex(i);
      fallRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="lernen-tab">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_240px]">
        {d.kennzahl ? <LernKennzahlKarte k={d.kennzahl} /> : <div />}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
          <Card className="flex flex-col justify-center gap-1 p-4">
            <span className="font-headline-md text-[28px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">{d.ausgewertet}</span>
            <span className="font-body-sm text-[12.5px] text-on-surface-variant">Angebote ausgewertet, bei denen du einen KI-Entwurf gesendet hast</span>
          </Card>
          <Card className="flex flex-col justify-center gap-1 p-4">
            <span className="font-headline-md text-[28px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">{aktiv}</span>
            <span className="font-body-sm text-[12.5px] text-on-surface-variant">{aktiv === 1 ? "Schluss aktiv" : "Schlüsse aktiv"}{offen ? `, ${offen} ${offen === 1 ? "wartet" : "warten"} auf dein Urteil` : ""}</span>
          </Card>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-start">
        <Card className="space-y-3 p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">Was ich aus deinen Änderungen schließe</h2>
            <span className="text-[12px] text-on-surface-variant">sortiert nach Urteil und Belegen</span>
          </div>
          {neuerHinweis && <HinweisForm onFertig={() => { onHinweisZu(); q.reload(); }} onAbbrechen={onHinweisZu} />}
          {sichtbar.length === 0 ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              Noch nichts gelernt. Sobald du KI-Entwürfe änderst und sendest, zähle ich, was du wiederholt anders machst — ab {d.schwelle.minBelege} Belegen wird daraus ein Schluss, den du beurteilst.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {sichtbar.map((s) => (
                <SchlussKarte key={s.id} s={s} schwelle={d.schwelle} rundung={d.rundung} onFertig={q.reload} onBeleg={zeigeFall} onVerlauf={setVerlauf} />
              ))}
            </div>
          )}
          {abgelehnt.length > 0 && (
            <div className="pt-1">
              <button type="button" className="text-[12.5px] font-semibold text-primary hover:underline" aria-expanded={zeigeAbgelehnt} onClick={() => setZeigeAbgelehnt((v) => !v)}>
                {zeigeAbgelehnt ? "Abgelehnte ausblenden" : `${abgelehnt.length} mit „Stimmt nicht“ markiert — anzeigen`}
              </button>
              {zeigeAbgelehnt && (
                <div className="mt-3 flex flex-col gap-3">
                  {abgelehnt.map((s) => (
                    <SchlussKarte key={s.id} s={s} schwelle={d.schwelle} rundung={d.rundung} onFertig={q.reload} onBeleg={zeigeFall} onVerlauf={setVerlauf} />
                  ))}
                </div>
              )}
            </div>
          )}
          <p className="text-[12px] text-on-surface-variant">
            Die Zahlen zähle ich aus deinen gesendeten Angeboten — Tests und Angebote mit „Nicht zum Lernen verwenden“ zählen nicht. Bestätigte Schlüsse und deine Hinweise lese ich beim Komponieren als Hinweise mit, zusätzlich zu den ähnlichen Fällen. Wende ich einen Schluss an, steht im Entwurf „Angewendet: R3“ — aber nur, wenn die Änderung dort wirklich drinsteht.
          </p>
        </Card>

        <div ref={fallRef} className="scroll-mt-24">
          <Card className="space-y-3 p-5">
            <h2 className="font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">KI-Vorschlag → deine Fassung</h2>
            {faelle.length === 0 ? (
              <p className="font-body-sm text-body-sm text-on-surface-variant">Noch kein gesendeter KI-Entwurf. Hier siehst du danach je Angebot, was du am Vorschlag geändert hast.</p>
            ) : (
              <FallAnsicht faelle={faelle} index={Math.min(index, faelle.length - 1)} setIndex={setIndex} onFertig={q.reload} />
            )}
          </Card>
        </div>
      </div>
      <VerlaufDrawer s={verlauf} onClose={() => setVerlauf(null)} onFertig={q.reload} />
    </div>
  );
}
